// src/pages/CartPage.tsx
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();

  const total = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

  /* ------------------------ handlers ------------------------ */
  const handleCheckout = () => {
    if (cart.length === 0) return;

    const [{ paymentLinkUrl, _id, size }] = cart;

    const ref = `${_id}_${size}`;

    if (!paymentLinkUrl?.startsWith("https://buy.stripe.com/")) {
      alert(
        "Mauvais lien : il faut une URL https://buy.stripe.com/… (Payment Link)"
      );
      return;
    }

    const url = new URL(paymentLinkUrl);
    url.searchParams.set("client_reference_id", ref);
    window.location.assign(url.toString());
  };

  /* ------------------------ empty state --------------------- */
  if (cart.length === 0) {
    return (
      <section className="max-w-4xl mx-auto py-16 px-6 text-center">
        <h2 className="text-3xl font-bold mb-4">Votre panier est vide 🛒</h2>

        <button
          onClick={() => navigate("/articles")}
          className="bg-black text-white px-6 py-3 rounded-lg text-xl font-semibold hover:bg-gray-900 transition"
        >
          Articles à la une
        </button>
      </section>
    );
  }

  /* ------------------------ main render --------------------- */
  return (
    <section className="max-w-5xl mx-auto py-12 px-6">
      <h2 className="text-3xl font-bold mb-8">Votre panier</h2>

      <div className="space-y-6">
        {cart.map((item) => (
          <CartRow
            key={`${item._id}-${item.size}`}
            item={item}
            onQtyChange={(q) => updateQuantity(item._id, item.size, q)}
            onRemove={() => removeFromCart(item._id, item.size)}
          />
        ))}
      </div>

      {/* total + actions */}
      <div className="mt-8 flex justify-between items-center">
        <p className="text-xl font-semibold">
          Total&nbsp;: {total.toFixed(2)} €
        </p>

        <button onClick={clearCart} className="text-sm text-red-500 underline">
          Vider le panier
        </button>
      </div>

      <div className="mt-6 text-right">
        <button
          onClick={handleCheckout}
          className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800 transition"
        >
          Valider la commande
        </button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* ------------------------ sous-composants -------------------------- */

type RowProps = {
  item: ReturnType<typeof useCart>["cart"][number];
  onQtyChange: (qty: number) => void;
  onRemove: () => void;
};

function CartRow({ item, onQtyChange, onRemove }: RowProps) {
  return (
    <div className="flex items-center justify-between border-b pb-4">
      {/* gauche : visuel + infos */}
      <div className="flex items-center gap-4">
        <img
          src={item.image}
          alt={item.title}
          className="w-20 h-20 object-cover rounded"
          loading="lazy"
        />

        <div>
          <h3 className="font-semibold">{item.title}</h3>
          <p className="text-sm text-gray-600">Taille&nbsp;: {item.size}</p>

          {/* quantité */}
          <input
            type="number"
            min={1}
            value={item.quantity}
            onChange={(e) => onQtyChange(Number(e.target.value) || 1)}
            className="mt-1 w-12 border rounded text-center"
          />
        </div>
      </div>

      {/* droite : prix + action supprimer */}
      <div className="flex items-center gap-4">
        <span className="text-sm">
          {(item.price * item.quantity).toFixed(2)} €
        </span>

        <button
          onClick={onRemove}
          className="text-red-500 hover:underline text-sm"
        >
          Supprimer
        </button>
      </div>
    </div>
  );
}
